import { useCallback } from 'react';
import {
  SubmitErrorHandler,
  SubmitHandler,
  useFormContext,
} from 'react-hook-form';
import { createYupAugmentedResolver } from '../../resolver/createYupAugmentedResolver';
import { useFormAugmentedContext } from '../augmented/FormAugmentedContext';

export interface IHandleDraftSubmitParams {
  onValid: (data: any) => Promise<void>;
  extraContext?: Record<string, any>;
  touchFields?: boolean;
}

/**
 * Permette di chiamare da codice il submit del form. Ritorna un handler che gestisce la costruzione
 * della funzione di submit in maniera speculare a quello che viene fatto `Form.tsx`
 */
export function useHandleSubmit(params: IHandleDraftSubmitParams) {
  const { onValid, extraContext, touchFields } = params;

  const augmented = useFormAugmentedContext();
  const methods = useFormContext();

  const handler = useCallback(async () => {
    // Creo il contesto di validazione estendendolo con possibili parametri aggiuntivi
    const validationContext = {
      ...augmented.validationContext,
      ...extraContext,
    };

    // Creo le due funzioni che verranno chiamate in caso di validazione corretta o errata
    const handleValid: SubmitHandler<any> = async rawValues => {
      augmented.invalidSubject.next({ type: 'valid', data: rawValues });
      const values = await augmented.schema.validate(rawValues, {
        context: validationContext,
      });

      await onValid(values);
    };

    const handleInvalid: SubmitErrorHandler<any> = errors => {
      augmented.invalidSubject.next({ type: 'invalid', errors });
    };

    // Utile per il widget di voto, che altrimenti non mostra gli errori sugli input
    if (touchFields) {
      await methods.handleSubmit(handleValid, handleInvalid)();
      return;
    }

    // Creo il resolver allo stesso modo di useForm, necessario per ritornare la bag degli errori.
    const resolver = createYupAugmentedResolver(augmented.schema);

    // Recupero i valori del form
    const values = methods.getValues();

    // Valido i dati con lo stesso contesto ma con un tipo di validazione diverso (draft)
    const result = await resolver(values, validationContext, {
      abortEarly: false,
    });

    // Se non ci sono errori, chiamo la funzione di validazione corretta, altrimenti chiamo quella di errore
    if (Object.keys(result.errors).length === 0) {
      await handleValid(result.values);

      // Attivando il `clearErrors`, gli errori vengono rimossi, ma questo
      // non è il comportamento che vogliamo. Vogliamo che gli errori rimangano
      // visibili, anche se il salvataggio è andato a buon fine.
      // methods.clearErrors();
    } else {
      // La validazione draft non dovrebbe in linea di massima restituire errori, ma nel caso
      // in cui ciò accada, li imposto sul form così che siano gestiti.
      Object.keys(result.errors).forEach(key => {
        const error = (result.errors as any)[key];
        methods.setError(key, error);
      });
      await handleInvalid(result.errors);
    }
  }, [
    augmented.invalidSubject,
    augmented.schema,
    augmented.validationContext,
    extraContext,
    methods,
    onValid,
    touchFields,
  ]);

  return handler;
}
